// FLOW-01 (task 5.3, D17) — o estado ao vivo de cada nó, a partir dos eventos de depuração.
//
// Os eventos são os que o PowerTools emite (`powertools/powertools/core/flow/depuracao.py`,
// `Sessao._evento`) e o `FlowDepuracaoChannel` transmite em lote (`data.eventos`):
// `{seq, tipo, em, caminho?, type?, ...}`. O canvas lê:
// - `passo`: `status` (`running`, `ok`, `failed`, `skipped`, `cancelled` e, no bloco que o depurador
//   cortou por dentro, `stopped`), `itens` (quantos itens saíram, para a conexão dizer "N itens") e
//   `fixado` (a saída fixada, o "pin data" do n8n);
// - `pausado` e `retomado`: o passo em que a sessão parou e esperou;
// - `parado`: a sessão parada pelo depurador (ou expirada) no portão de um passo. FLOW-12 (Gate 4):
//   esse passo e os de depois dele não rodaram porque a pessoa pediu (`parouEm`);
// - `fim`: o estado final da sessão.
//
// A pausa que acaba em `parar` ou por expiração não emite `retomado` (`_esperar` só o emite quando
// a sessão segue): o `parado` e o `fim` também a tiram, senão o nó ficava "Pausado" depois do fim.
//
// Os lotes podem chegar fora de ordem (cada um é um POST), então quem decide é o `seq`. O último
// evento de cada passo vale: o `running` é trocado pelo `ok` quando ele chega.
//
// FLOW-04 (PLAN P4): dentro de laço, o evento traz o caminho da volta (`steps[resolve]{2}.steps[b]`,
// com `volta: [2]`), e o nó do canvas tem o caminho estático (`steps[resolve].steps[b]`): o estado é
// do nó, e a última volta vale. O nó do laço guarda `voltas` (`{feitas, total}`), o "volta 3 de 8"
// que ele emite no começo de cada volta.
export const caminhoEstatico = caminho =>
  typeof caminho === 'string' ? caminho.replace(/\{\d+\}/g, '') : caminho;

export function estadosDaExecucao(eventos) {
  const porCaminho = {};
  let pausadoEm = null;
  let parouEm = null;
  let fim = null;
  [...(eventos || [])]
    .sort((a, b) => a.seq - b.seq)
    .forEach(evento => {
      const caminho = caminhoEstatico(evento.caminho);
      if (evento.tipo === 'passo') {
        const voltas = evento.voltas ?? porCaminho[caminho]?.voltas;
        porCaminho[caminho] = {
          status: evento.status,
          itens: evento.itens ?? null,
          fixado: !!evento.fixado,
          ...(voltas ? { voltas } : {}),
        };
        if (pausadoEm === caminho) pausadoEm = null;
      } else if (evento.tipo === 'pausado') {
        pausadoEm = caminho;
      } else if (['retomado', 'parado'].includes(evento.tipo)) {
        pausadoEm = null;
        if (evento.tipo === 'parado') parouEm = caminho;
      } else if (evento.tipo === 'fim') {
        pausadoEm = null;
        fim = evento.estado;
      }
    });
  return { porCaminho, pausadoEm, parouEm, fim };
}

// Quantos itens passaram pela conexão: os que o passo de cima produziu, quando o de baixo também
// rodou. A conexão do ramo que o `if` não tomou fica sem número, como no n8n.
export function itensNaConexao({ porCaminho }, origem, destino) {
  const de = porCaminho[origem];
  if (!de || de.status !== 'ok' || de.itens == null || !porCaminho[destino]) {
    return null;
  }
  return de.itens;
}

// FLOW-12 (Task 5.2, T4, G11): o estado da linha de status embaixo do nó, pela precedência da G11:
// rodando, mudou, falhou, fixado, ok, não rodou. `status` é o do último evento do passo (ou da volta
// escolhida), `fixado` a saída fixada (a do evento ou a de quem testa), `mudou` o "Mudou depois da
// execução" (G3). Nulo: o nó não tem o que dizer (nunca rodou nem foi fixado).
export function estadoDaLinha({
  status = null,
  fixado = false,
  mudou = false,
}) {
  if (status === 'running') return 'rodando';
  if (mudou && status) return 'mudou';
  if (status === 'failed') return 'falhou';
  if (fixado) return 'fixado';
  if (status === 'ok') return 'ok';
  if (status === 'skipped' || status === 'cancelled') return 'pulado';
  return null;
}

// A falha na linha do nó: a primeira linha da mensagem, até 80 caracteres (T4); inteira na dica.
export const TETO_DA_FALHA = 80;
export function primeiraLinha(texto, teto = TETO_DA_FALHA) {
  const linha = String(texto ?? '')
    .split('\n')[0]
    .trim();
  const caracteres = [...linha];
  return caracteres.length <= teto
    ? linha
    : `${caracteres.slice(0, teto - 1).join('')}…`;
}
